import UserPage from 'flarum/components/UserPage';
import Stream from 'flarum/common/utils/Stream';
import Button from 'flarum/common/components/Button';
import Dropdown from 'flarum/common/components/Dropdown';
import TagSelectionModal from 'flarum/tags/common/components/TagSelectionModal';
import app from 'flarum/forum/app';

export default class JeepytaFilesSettingsPage extends UserPage {
  oninit(vnode) {
    super.oninit(vnode);

    this.loadUser(m.route.param('username'));

    // State management for files
    this.files = [];
    this.isLoading = true;

    // prompts: { [slug]: Stream<string> }
    this.prompts = {};

    // response modes (keyed by tagId): { [tagId]: Stream<string> }
    this.responseModes = {};

    // Labels for dropdown
    this.RESPONSE_MODE_LABELS = {
      no_response: 'No response',
      respond_each_hide: 'Respond to each post but hide',
      respond_first: 'Respond only to the first post',
      respond_first_hide_next: 'Respond to the first post and hide next responses',
      respond_each_public: 'Respond to each post publicly',
    };

    // Fetch files from the API
    app.store.find('files').then((files) => {
      this.files = files;
      this.isLoading = false;
      m.redraw();
    });

    // Ensure tags are loaded before using app.store.all('tags')
    app.store.find('tags').then(() => {
      this.initPromptsFromForumSettings();
      m.redraw();
    });
  }

  initPromptsFromForumSettings() {
    // persona is decoded to object via serializeToForum
    const persona = app.forum.attribute('blomstra-support-ai.persona') || {};
    // mapping tagId -> mode, decoded to object via serializeToForum
    const modeByTagId = app.forum.attribute('blomstra-support-ai.tag-response-modes') || {};

    const tags = app.store.all('tags');

    this.prompts = {};
    this.responseModes = {};

    tags.forEach((tag) => {
      const slug = tag.slug();
      const tagId = String(tag.id());

      this.prompts[slug] = Stream(persona?.[slug]?.prompt || '');
      this.responseModes[tagId] = Stream(modeByTagId?.[tagId] || 'no_response');
    });
  }

  setTagResponseMode(tagId, mode) {
    tagId = String(tagId);

    return app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/jeepyta-tag-mode',
        body: { tag_id: tagId, mode },
      })
      .then(() => {
        if (this.responseModes[tagId]) {
          this.responseModes[tagId](mode);
        }

        // Keep forum attribute mapping in sync
        const current = app.forum.attribute('blomstra-support-ai.tag-response-modes') || {};
        const updated = { ...current, [tagId]: mode };
        app.forum.pushAttributes({
          'blomstra-support-ai.tag-response-modes': updated,
        });

        app.alerts.show({ type: 'success' }, 'Response option updated');
        m.redraw();
      })
      .catch(() => {
        app.alerts.show({ type: 'error' }, 'Failed to update response option');
      });
  }

  content() {
    const tags = app.store.all('tags');
    const user = app.session.user;

    if (user.isAdmin() || user.attribute('canAccessJeepyta')) {
      return (
        <div className="SettingsPage">
          <div className="container">

            {/* ---- PROMPTS + PER-TAG RESPONSE OPTION ---- */}
            <div className="Form">
              <div className="JeepytaFilesSettingsPage">
                <h2>JeepyTA - prompts</h2>
                <p>Adjust your JeepyTA prompts and response behavior per tag.</p>
              </div>

              {tags.map((tag) => {
                const slug = tag.slug();
                const tagId = String(tag.id());

                const promptStream = this.prompts[slug];
                const modeStream = this.responseModes[tagId];

                const currentMode = modeStream ? modeStream() : 'no_response';
                const currentLabel = this.RESPONSE_MODE_LABELS[currentMode] || currentMode;

                return (
                  <div className="JeepytaSettingsBlock">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <h3 style={{ margin: 0 }}>{tag.name()}</h3>

                      {m(
                        Dropdown,
                        {
                          buttonClassName: 'Button Button--default',
                          label: currentLabel,
                          caretIcon: 'fas fa-caret-down',
                        },
                        [
                          m(
                            'button',
                            {
                              className: 'Dropdown-item',
                              type: 'button',
                              onclick: () => this.setTagResponseMode(tagId, 'no_response'),
                            },
                            'No response'
                          ),
                          m(
                            'button',
                            {
                              className: 'Dropdown-item',
                              type: 'button',
                              onclick: () => this.setTagResponseMode(tagId, 'respond_each_hide'),
                            },
                            'Respond to each post but hide'
                          ),
                          m(
                            'button',
                            {
                              className: 'Dropdown-item',
                              type: 'button',
                              onclick: () => this.setTagResponseMode(tagId, 'respond_first'),
                            },
                            'Respond only to the first post'
                          ),
                          m(
                            'button',
                            {
                              className: 'Dropdown-item',
                              type: 'button',
                              onclick: () => this.setTagResponseMode(tagId, 'respond_first_hide_next'),
                            },
                            'Respond to the first post and hide next responses'
                          ),
                          m(
                            'button',
                            {
                              className: 'Dropdown-item',
                              type: 'button',
                              onclick: () => this.setTagResponseMode(tagId, 'respond_each_public'),
                            },
                            'Respond to each post publicly'
                          ),
                        ]
                      )}
                    </div>

                    <textarea
                      rows="10"
                      style={{ width: '80%', marginTop: '8px' }}
                      value={promptStream ? promptStream() : ''}
                      oninput={(e) => promptStream && promptStream(e.target.value)}
                    />
                  </div>
                );
              })}

              {/* Save prompts only; response mode saves immediately via /jeepyta-tag-mode */}
              <Button className="Button Button--primary" onclick={this.save.bind(this)}>
                Save prompts
              </Button>
            </div>

            {/* ---- FILES ---- */}
            <div className="Form">
              <div className="JeepytaFilesSettingsPage">
                <h2>JeepyTA - files</h2>
                <p>Adjust your JeepyTA files below</p>
                <h3>List of files</h3>
              </div>

              {this.isLoading
                ? m('div', 'Loading files...')
                : this.files.map((file) => this.renderFile(file, 'manage'))}
            </div>
          </div>
        </div>
      );
    }

    // Non-admin / non-access users: view only public files
    return (
      <div className="SettingsPage">
        <div className="container">
          <div className="Form">
            <div className="JeepytaFilesSettingsPage">
              <h2>JeepyTA - files</h2>
            </div>

            {this.isLoading
              ? m('div', 'Loading files...')
              : this.files
                .filter((file) => file.hidden() === false)
                .map((file) => this.renderFile(file, 'view'))}
          </div>
        </div>
      </div>
    );
  }

  save() {
    const newSettings = {};

    Object.keys(this.prompts).forEach((slug) => {
      newSettings[slug] = {
        prompt: this.prompts[slug](),
      };
    });

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/jeepyta-settings',
        body: { 'blomstra-support-ai.persona': JSON.stringify(newSettings) },
      })
      .then((response) => {
        if (response.saved) {
          app.alerts.show({ type: 'success' }, 'Settings saved');
        } else {
          app.alerts.show({ type: 'error' }, 'Failed to save settings');
        }
      })
      .catch((error) => {
        if (error.response && error.response.error) {
          app.alerts.show({ type: 'error' }, error.response.error);
        } else {
          app.alerts.show({ type: 'error' }, 'Error while saving settings');
        }
      });
  }

  renderFile(file, permission) {
    const associatedTags = file.data.attributes.associatedTags || [];
    const tagModels = associatedTags
      .map((tagId) => app.store.getById('tags', tagId))
      .filter(Boolean);

    if (permission === 'manage') {
      return m('div', { className: 'FileItem' }, [
        m('label', [
          '',
          m('a', { className: 'FileLink', href: file.url(), download: file.baseName() }, file.baseName()),
          file.attribute('hasEmbeddings') !== false
            ? m('i', { className: 'icon fas fa-circle green-circle' })
            : m('i', { className: 'icon fas fa-circle gray-circle' }),
          file.attribute('hidden') == true
            ? m('i', {
              className: 'icon fas fa-eye-slash private-icon',
              'aria-label': 'Private File',
              'data-original-title': 'Private File',
            })
            : m('i', {
              className: 'icon fas fa-eye public-icon',
              'aria-label': 'Public File',
              'data-original-title': 'Public File',
            }),
        ]),

        m(
          'div',
          { className: 'TagList' },
          tagModels.length
            ? tagModels
              .sort((a, b) => String(a.id()).localeCompare(String(b.id())))
              .map((tag) => m('span', { className: 'TagBadge' }, tag.name()))
            : m('span', { className: 'NoTags' }, 'No tags selected')
        ),

        Button.component(
          {
            className: 'Button Button--primary',
            onclick: () => {
              app.modal.show(TagSelectionModal, {
                selectedTags: tagModels,
                onsubmit: (selectedTags) => {
                  const tagIds = selectedTags.map((tag) => tag.id());

                  app
                    .request({
                      method: 'POST',
                      url: app.forum.attribute('apiUrl') + `/files/${file.id()}/tags`,
                      body: { tag_ids: tagIds },
                    })
                    .then(() => {
                      app.alerts.show({ type: 'success' }, 'Tags updated successfully');
                      file.pushAttributes({ associatedTags: tagIds });
                    })
                    .catch(() => {
                      app.alerts.show({ type: 'error' }, 'Failed to update tags');
                    });
                },
              });
            },
          },
          'Choose Tags'
        ),
      ]);
    }

    // view
    return m('div', { className: 'FileItem' }, [
      m('label', [
        '',
        file.hidden() === false
          ? m('a', { className: 'FileLink', href: file.url(), download: file.baseName() }, file.baseName())
          : null,
      ]),
      m(
        'div',
        { className: 'TagList' },
        tagModels.length
          ? tagModels
            .sort((a, b) => String(a.id()).localeCompare(String(b.id())))
            .map((tag) => m('span', { className: 'TagBadge' }, tag.name()))
          : m('span', { className: 'NoTags' }, 'No tags selected')
      ),
    ]);
  }
}
